/**
 * Form Row Component
 * The new form row component is now responsible for handling form row status indicator
 *
 * Params:
 *
 */
import { Component, inject, input, output } from '@angular/core';
import { FORM_HOST, type FormHost } from '../form.contract';
import { FormComponent } from '../form.component';

import type { OnInit, OnDestroy } from '@angular/core';
import type { NgModel } from '@angular/forms';
import type { FormRowStatus } from '../domain/form.domain';

@Component({
  template: '<ng-content />',
  styleUrls: ['./form-row.component.scss'],
  selector: 'td-wb-form-row',
  host: {
    '[class.td-wb-form__row--active]': 'focused',
    '[class.td-wb-row-error]': 'error',
    '[class.td-wb-form__row__narrative]': 'isNarrative',
    '[class.td-wb-form__row--border-top]': 'showBorderTop()'
  }
})
export class FormRowComponent implements OnInit, OnDestroy {
  private form: FormHost | FormComponent | null = inject(FORM_HOST, { optional: true })
    ?? inject(FormComponent, { optional: true });

  focused: boolean = false;
  error: boolean = false;
  isNarrative: boolean = false;

  readonly showBorderTop = input<boolean>(false);

  readonly rowName = input<string>(undefined);

  readonly rowStatusChange = output<FormRowStatus>();

  id: number = 0;
  private inputControls: Array<NgModel> = new Array<NgModel>();
  private readonly externalStatuses = new Map<string, { dirty: boolean; valid: boolean }>();
  private formRowStatus: FormRowStatus;

  ngOnInit(): void {
    this.isNarrative = this.form?.formType() === 'narrative';
    this.form?.addRow(this);
  }

  ngOnDestroy(): void {
    this.form?.removeRow(this);
    if (this.isNarrative && this.form !== null) {
      this.inputControls.forEach((inputModel: NgModel): void => {
        this.form?.formElement().removeControl(inputModel);
      });
      this.externalStatuses.clear();
    }
  }

  setExternalControlStatus(controlName: string, dirty: boolean, valid: boolean): void {
    if (!this.isNarrative || this.rowName() === undefined) {
      return;
    }
    this.externalStatuses.set(controlName, { dirty, valid });
    this.emitAggregatedRowStatus();
  }

  clearExternalControlStatus(controlName: string): void {
    if (!this.isNarrative || this.rowName() === undefined) {
      return;
    }
    this.externalStatuses.delete(controlName);
    this.emitAggregatedRowStatus();
  }

  registerFormControl(inputModel: NgModel): void {
    if (this.isNarrative) {
      this.inputControls.push(inputModel);
      if (this.rowName() !== undefined) {
        inputModel.statusChanges.subscribe((): void => {
          this.setRowStatus();
        });
      }
    }
  }

  unRegisterFormControl(inputModel: NgModel): void {
    if (this.isNarrative) {
      const index = this.inputControls.indexOf(inputModel);
      if (index !== -1) {
        this.inputControls.splice(index, 1);
      }
    }
  }

  private setRowStatus(): void {
    this.emitAggregatedRowStatus();
  }

  private emitAggregatedRowStatus(): void {
    const rowStatus: FormRowStatus = {
      name: this.rowName(),
      dirty: true,
      valid: true
    };
    this.inputControls.forEach((model: NgModel): void => {
      if (!model.dirty) {
        rowStatus.dirty = false;
      }
      if (!model.valid) {
        rowStatus.valid = false;
      }
    });

    this.externalStatuses.forEach((status: { dirty: boolean; valid: boolean }): void => {
      if (!status.dirty) {
        rowStatus.dirty = false;
      }
      if (!status.valid) {
        rowStatus.valid = false;
      }
    });

    if (this.formRowStatus === undefined ||
      this.formRowStatus.dirty !== rowStatus.dirty ||
      this.formRowStatus.valid !== rowStatus.valid) {
      this.formRowStatus = rowStatus;
      this.rowStatusChange.emit(rowStatus);
    }

    if (rowStatus.dirty || rowStatus.valid) {
      this.form?.setRowStatus(rowStatus);
    }
  }
}
